import React, { useEffect, useRef, useState } from 'react';
import {
  Button,
  Dropdown,
  Form,
  Input,
  Label,
  Loader,
  Menu,
  Message,
  Modal,
  Segment,
  Table,
} from 'semantic-ui-react';
import GlassPageWrapper from '../../components/Layout/GlassPageWrapper';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/useToast';
import { getContractStatusText } from '../../utils/contractStatusUtils';
import cqvVtmeAssignmentService, {
  CQV_VTME_ANALYST_DISCLAIMER,
  CqvVtmeAnalystDetailDto,
  CqvVtmeAnalystItemDto,
  CqvVtmeAssignmentContractDto,
  CqvVtmeAssignmentEventDto,
  CqvVtmeAssignmentListQuery,
  CqvVtmeAssignmentListResponseDto,
  CqvVtmeAssignmentReportDto,
  CqvVtmeAssignmentRowDto,
} from '../../services/cqv/cqvVtmeAssignmentService';
import CqvVtmePeriodChips, { CqvVtmePeriodPreset } from './CqvVtmePeriodChips';
import CqvVtmeQueueCards from './CqvVtmeQueueCards';
import CqvVtmePeriodSkeleton from './CqvVtmePeriodSkeleton';
import CqvVtmeLinesSummary from './CqvVtmeLinesSummary';
import CqvVtmeReportTab from './CqvVtmeReportTab';
import {
  CQV_VTME_MODAL_CSS,
  CQV_VTME_THEME,
  cqvVtmeModalActionsStyle,
  cqvVtmeModalContentStyle,
  cqvVtmeModalHeaderStyle,
  cqvVtmePageStyle,
  cqvVtmeSegmentStyle,
} from './cqvVtmePageTheme';
import { formatCqvAnalystDisplayName } from './formatCqvAnalystDisplayName';
import { saoPauloFilterRangeToUtcIso, saoPauloPresetRange } from '../../utils/saoPauloFilterBounds';
import { describeCqvVtmeSnapshotBanner } from './cqvVtmeSnapshotBanner';
import CqvVtmeSlaDigestSettings from './CqvVtmeSlaDigestSettings';
import {
  cqvVtmeLoadRequestsFresh,
  nextPreferCqvVtmeFresh,
  withCqvVtmeFreshFlag,
} from './cqvVtmeFreshRequest';
import {
  decideCqvVtmeCardPublish,
  shouldHoldCqvVtmeCardsUntilBothSettle,
  type CqvVtmeLoadReason,
} from './cqvVtmeCardPublish';
import { AbortableRequestManager } from '../../hooks/contracts/abortableRequest';
import { isCanceledError } from '../../services/utils/apiErrorUtils';

const INITIAL_PERIOD = saoPauloPresetRange('today');

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
};

const STATUS_OPTIONS = [
  { key: 35, value: 35, text: 'Aprovado CQV' },
  { key: 39, value: 39, text: 'VTME Criado' },
  { key: 66, value: 66, text: 'Qualidade OK' },
  { key: 67, value: 67, text: 'Aprovado CCV' },
];

/** Shared Semantic Dropdown props: searchable, clearable, no blur-select, scrollable menu. */
const CQV_VTME_SELECT_PROPS = {
  search: true,
  clearable: true,
  selection: true,
  selectOnBlur: false,
  scrolling: true,
} as const;

const CQV_VTME_DROPDOWN_CSS = `
  .cqv-vtme-filters.ui.segment,
  .cqv-vtme-filters .ui.form,
  .cqv-vtme-filters .ui.form .fields,
  .cqv-vtme-filters .ui.form .field {
    overflow: visible !important;
  }
  .cqv-vtme-filters .ui.dropdown {
    z-index: 1;
  }
  .cqv-vtme-filters .ui.dropdown.active {
    z-index: 20;
  }
  .cqv-vtme-filters .ui.dropdown .menu {
    z-index: 21 !important;
    max-height: 280px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    top: 100% !important;
    bottom: auto !important;
  }
  .cqv-vtme-filters .ui.dropdown.upward .menu {
    top: auto !important;
    bottom: 100% !important;
  }
  .cqv-vtme-transfer-modal.ui.modal {
    overflow: visible !important;
  }
  .cqv-vtme-transfer-modal > .content {
    overflow: visible !important;
  }
  .cqv-vtme-transfer-modal .ui.dropdown.active {
    z-index: 100051 !important;
  }
  .cqv-vtme-transfer-modal .ui.dropdown .menu,
  .cqv-vtme-transfer-modal .ui.dropdown.active .menu.visible {
    z-index: 100051 !important;
    max-height: 280px !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    top: 100% !important;
    bottom: auto !important;
    margin-top: 0 !important;
  }
  .cqv-vtme-transfer-modal .ui.dropdown.upward .menu {
    top: auto !important;
    bottom: 100% !important;
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
`;

const eventLabel = (ev: CqvVtmeAssignmentEventDto) => {
  if (ev.kind === 'status') {
    const from = ev.previousStatusLabel || (ev.previousStatus != null ? getContractStatusText(ev.previousStatus) : '—');
    const to = ev.statusLabel || (ev.status != null ? getContractStatusText(ev.status) : ev.type);
    return `${from} → ${to}`;
  }
  if (ev.type === 'Assigned') return 'Atribuído';
  if (ev.type === 'EmailSent') return 'E-mail enviado';
  if (ev.type === 'Transferred') return 'Transferido';
  if (ev.type === 'Removed') return 'Removido';
  return ev.type;
};

const relationLabel = (relation?: string) => {
  if (relation === 'current') return 'Na fila';
  if (relation === 'transferredAway') return 'Transferida';
  if (relation === 'removed') return 'Removida';
  if (relation === 'former') return 'Esteve';
  return relation || '—';
};

const dash = (value?: string | null) => (value && value.trim() ? value : '—');

/** Contrato do grupo em correção há mais tempo (RN08), + quantos outros também estão em correção. */
const oldestCorrectionContract = (
  contracts: CqvVtmeAssignmentContractDto[]
): { contract: CqvVtmeAssignmentContractDto; extraCount: number } | null => {
  const inCorrection = contracts.filter(c => c.isInCorrection);
  if (inCorrection.length === 0) return null;
  const sorted = [...inCorrection].sort((a, b) => {
    const ta = a.correctionStartedAtUtc ? new Date(a.correctionStartedAtUtc).getTime() : Number.POSITIVE_INFINITY;
    const tb = b.correctionStartedAtUtc ? new Date(b.correctionStartedAtUtc).getTime() : Number.POSITIVE_INFINITY;
    return ta - tb;
  });
  return { contract: sorted[0], extraCount: sorted.length - 1 };
};

type CqvVtmeTransferTarget = {
  id: string;
  assignedUserId: string;
};

const CqvVtmeActionGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="cqv-vtme-action-group">{children}</div>
);

const CqvVtmeAssignmentsPage: React.FC = () => {
  const { hasRole } = useAuth();
  const { showSuccess, showError } = useToast();
  const isAdmin = hasRole('Admin');

  const [loading, setLoading] = useState(false);
  /** Filtro/carga inicial: esconde números do período anterior até lista e relatório assentarem juntos. */
  const [contentHold, setContentHold] = useState(true);
  const [data, setData] = useState<CqvVtmeAssignmentListResponseDto | null>(null);
  const [report, setReport] = useState<CqvVtmeAssignmentReportDto | null>(null);
  const [listError, setListError] = useState(false);
  const [reportError, setReportError] = useState(false);
  const [activeTab, setActiveTab] = useState<'fila' | 'relatorios'>('fila');
  const [assignedUserId, setAssignedUserId] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [periodPreset, setPeriodPreset] = useState<CqvVtmePeriodPreset>('today');
  const [fromLocal, setFromLocal] = useState(INITIAL_PERIOD.fromLocal);
  const [toLocal, setToLocal] = useState(INITIAL_PERIOD.toLocal);
  const [consultantUserId, setConsultantUserId] = useState<string | undefined>();
  const [statuses, setStatuses] = useState<number[]>([]);
  const [appliedQuery, setAppliedQuery] = useState<CqvVtmeAssignmentListQuery>(() => {
    const { fromUtc, toUtc } = saoPauloFilterRangeToUtcIso(INITIAL_PERIOD.fromLocal, INITIAL_PERIOD.toLocal);
    return { fromUtc, toUtc };
  });

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyEvents, setHistoryEvents] = useState<CqvVtmeAssignmentEventDto[]>([]);

  const [transferRow, setTransferRow] = useState<CqvVtmeTransferTarget | null>(null);
  const [toUserId, setToUserId] = useState<string>('');
  const [transferring, setTransferring] = useState(false);
  const [analystOpen, setAnalystOpen] = useState(false);
  const [analystLoading, setAnalystLoading] = useState(false);
  const [analystDetail, setAnalystDetail] = useState<CqvVtmeAnalystDetailDto | null>(null);
  const [removeTarget, setRemoveTarget] = useState<CqvVtmeAssignmentRowDto | CqvVtmeAnalystItemDto | null>(null);
  const [removing, setRemoving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingList, setExportingList] = useState(false);
  const [exportingFull, setExportingFull] = useState(false);
  const [freshView, setFreshView] = useState(false);
  const [freshPending, setFreshPending] = useState(false);

  /** Chave do filtro atualmente pedido. Resposta de outro período/filtro é ignorada;
   * refresh do mesmo filtro não descarta a lista se o relatório ainda estiver pendente. */
  const appliedKeyRef = useRef('');
  const refreshInFlightRef = useRef(false);
  /** Filtro em curso: o auto-refresh não pode publicar por cima do skeleton. */
  const holdInFlightRef = useRef(false);
  /** Depois da primeira query concluída, trocar de aba não limpa os cartões. */
  const pageReadyRef = useRef(false);
  /** Query cujos números estão na tela. Diferente da pedida → skeleton até o par assentar. */
  const visibleKeyRef = useRef('');
  const pageAbortRef = useRef(new AbortableRequestManager());
  /** Depois de Atualizar agora / transferir / remover, o auto-refresh não volta ao snapshot. */
  const preferFreshRef = useRef(false);

  const buildQuery = (from = fromLocal, to = toLocal): CqvVtmeAssignmentListQuery => {
    const { fromUtc, toUtc } = saoPauloFilterRangeToUtcIso(from, to);
    return {
      assignedUserId,
      search: search.trim() || undefined,
      fromUtc,
      toUtc,
      consultantUserId,
      statuses: statuses.length ? statuses : undefined,
    };
  };

  const queryKey = (query: CqvVtmeAssignmentListQuery) =>
    JSON.stringify({
      assignedUserId: query.assignedUserId ?? '',
      fromUtc: query.fromUtc ?? '',
      toUtc: query.toUtc ?? '',
      search: query.search ?? '',
      consultantUserId: query.consultantUserId ?? '',
      statuses: [...(query.statuses ?? [])].sort((a, b) => a - b),
      fresh: query.fresh === true,
    });

  const load = async (override?: { fromLocal?: string; toLocal?: string; reason?: CqvVtmeLoadReason }) => {
    const reason = override?.reason ?? 'filter';
    const requestFresh = cqvVtmeLoadRequestsFresh(reason, preferFreshRef.current);
    preferFreshRef.current = nextPreferCqvVtmeFresh(reason, preferFreshRef.current);
    const query = withCqvVtmeFreshFlag(
      buildQuery(override?.fromLocal ?? fromLocal, override?.toLocal ?? toLocal),
      requestFresh
    );
    const key = queryKey(query);
    const visibleMatches = visibleKeyRef.current === key;
    const hold =
      shouldHoldCqvVtmeCardsUntilBothSettle(reason) || (reason !== 'refresh' && !visibleMatches);

    if (reason === 'refresh' && (refreshInFlightRef.current || holdInFlightRef.current)) return;

    setAppliedQuery(query);
    appliedKeyRef.current = key;
    setFreshPending(reason === 'fresh');

    // No filtro, a lista também é pedida na aba Relatórios, para o par assentar junto
    // e a Fila não reabrir com totais do período anterior.
    const fetchList = hold || activeTab === 'fila';
    const fetchReport = true;

    if (reason === 'refresh') {
      refreshInFlightRef.current = true;
      cqvVtmeAssignmentService.invalidatePageCache(query);
    } else if (hold) {
      holdInFlightRef.current = true;
      setContentHold(true);
      setLoading(true);
    } else if (reason === 'tab') {
      setContentHold(false);
    }

    const bypassCache = reason === 'filter' || reason === 'refresh' || reason === 'fresh';
    const signal = pageAbortRef.current.start();
    const isCurrent = () =>
      !signal.aborted && appliedKeyRef.current === key && pageAbortRef.current.isCurrent(signal);

    type ListOutcome = { ok: true; value: CqvVtmeAssignmentListResponseDto } | { ok: false };
    type ReportOutcome = { ok: true; value: CqvVtmeAssignmentReportDto } | { ok: false };
    let listOutcome: ListOutcome | undefined;
    let reportOutcome: ReportOutcome | undefined;
    let revealed = false;

    try {
      const tasks: Promise<unknown>[] = [];

      if (fetchList) {
        tasks.push(
          cqvVtmeAssignmentService
            .list(query, { bypassCache, signal })
            .then(list => {
              if (!isCurrent()) return;
              if (hold) {
                listOutcome = { ok: true, value: list };
                return;
              }
              setData(list);
              setListError(false);
              visibleKeyRef.current = key;
              setFreshView(requestFresh);
            })
            .catch((err: unknown) => {
              if (!isCurrent() || isCanceledError(err)) return;
              if (hold) {
                listOutcome = { ok: false };
                return;
              }
              setListError(true);
              if (reason !== 'refresh') showError('Erro ao carregar atribuições VTME');
            })
        );
      }

      if (fetchReport) {
        tasks.push(
          cqvVtmeAssignmentService
            .listReport(query, { bypassCache, signal })
            .then(nextReport => {
              if (!isCurrent()) return;
              if (hold) {
                reportOutcome = { ok: true, value: nextReport };
                return;
              }
              setReport(nextReport);
              setReportError(false);
              visibleKeyRef.current = key;
              setFreshView(requestFresh);
            })
            .catch((err: unknown) => {
              if (!isCurrent() || isCanceledError(err)) return;
              if (hold) {
                reportOutcome = { ok: false };
                return;
              }
              setReportError(true);
              if (reason !== 'refresh') showError('Erro ao carregar relatórios VTME');
            })
        );
      }

      await Promise.allSettled(tasks);
      if (!hold || !isCurrent()) return;

      if (fetchList && !listOutcome) listOutcome = { ok: false };
      if (fetchReport && !reportOutcome) reportOutcome = { ok: false };

      const decision = decideCqvVtmeCardPublish({
        reason,
        expectList: fetchList,
        expectReport: fetchReport,
        listSettled: listOutcome !== undefined,
        reportSettled: reportOutcome !== undefined,
      });
      if (!isCurrent()) return;
      if (decision.hold) {
        if (fetchList) setListError(true);
        setReport(null);
        setReportError(true);
        visibleKeyRef.current = key;
        setContentHold(false);
        revealed = true;
        return;
      }

      let published = false;
      if (decision.publishList && listOutcome) {
        if (listOutcome.ok) {
          setData(listOutcome.value);
          setListError(false);
          published = true;
        } else {
          setListError(true);
          showError('Erro ao carregar atribuições VTME');
        }
      }
      if (decision.publishReport && reportOutcome) {
        if (reportOutcome.ok) {
          setReport(reportOutcome.value);
          setReportError(false);
          published = true;
        } else {
          setReport(null);
          setReportError(true);
          showError('Erro ao carregar relatórios VTME');
        }
      }
      if (published) setFreshView(requestFresh);
      visibleKeyRef.current = key;
      setContentHold(false);
      revealed = true;
    } finally {
      if (reason === 'refresh') {
        refreshInFlightRef.current = false;
      }
      if (isCurrent()) {
        holdInFlightRef.current = false;
        if (hold && !revealed) {
          if (fetchList) setListError(true);
          setReport(null);
          setReportError(true);
          setContentHold(false);
        }
        setLoading(false);
        setFreshPending(false);
        pageReadyRef.current = true;
      }
    }
  };

  const applyPeriodPreset = (preset: CqvVtmePeriodPreset) => {
    if (preset === 'custom') {
      setPeriodPreset('custom');
      return;
    }
    const range = saoPauloPresetRange(preset === 'today' ? 'today' : Number(preset) as 7 | 14 | 30);
    setPeriodPreset(preset);
    setFromLocal(range.fromLocal);
    setToLocal(range.toLocal);
    void load({ ...range, reason: 'filter' });
  };

  const applyFilters = () => {
    if (periodPreset === 'custom') {
      if (!fromLocal || !toLocal) {
        showError('Informe o período de–até');
        return;
      }
      if (fromLocal > toLocal) {
        showError('A data final não pode ser anterior à inicial');
        return;
      }
    }
    void load({ reason: 'filter' });
  };

  const exportList = async () => {
    try {
      setExportingList(true);
      await cqvVtmeAssignmentService.exportList(appliedQuery);
    } catch (error: any) {
      showError(error?.message || 'Não foi possível exportar a lista');
    } finally {
      setExportingList(false);
    }
  };

  const exportFull = async () => {
    try {
      setExportingFull(true);
      await cqvVtmeAssignmentService.exportFull({
        fromUtc: appliedQuery.fromUtc,
        toUtc: appliedQuery.toUtc,
      });
    } catch (error: any) {
      showError(error?.message || 'Não foi possível exportar o relatório completo. Tente um período menor.');
    } finally {
      setExportingFull(false);
    }
  };

  /** loadRef sempre aponta pro load() mais recente (com os filtros atuais fechados no closure),
   * pra o auto-refresh nunca recarregar com um filtro velho. */
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!isAdmin) return;
    const reason: CqvVtmeLoadReason = pageReadyRef.current ? 'tab' : 'initial';
    void loadRef.current({ reason });
    return () => pageAbortRef.current.abortCurrent();
  }, [isAdmin, activeTab]);

  useEffect(() => {
    if (!isAdmin) return;
    const AUTO_REFRESH_MS = 2 * 60 * 1000;
    const interval = setInterval(() => {
      void loadRef.current({ reason: 'refresh' });
    }, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [isAdmin]);

  const openHistory = async (id: string) => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      const events = await cqvVtmeAssignmentService.getEvents(id);
      setHistoryEvents(events);
    } catch {
      showError('Erro ao carregar histórico');
      setHistoryEvents([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openAnalyst = async (userId: string) => {
    setAnalystOpen(true);
    setAnalystLoading(true);
    try {
      const detail = await cqvVtmeAssignmentService.getAnalyst(userId, appliedQuery);
      setAnalystDetail(detail);
    } catch {
      showError('Erro ao carregar o detalhe da analista');
      setAnalystDetail(null);
    } finally {
      setAnalystLoading(false);
    }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      await cqvVtmeAssignmentService.remove(removeTarget.id);
      showSuccess('Atribuição removida da fila');
      setRemoveTarget(null);
      await load({ reason: 'fresh' });
      if (analystDetail) {
        const detail = await cqvVtmeAssignmentService.getAnalyst(analystDetail.analyst.id, appliedQuery);
        setAnalystDetail(detail);
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Não foi possível remover');
    } finally {
      setRemoving(false);
    }
  };

  const exportAnalyst = async () => {
    if (!analystDetail) return;
    try {
      setExporting(true);
      await cqvVtmeAssignmentService.exportAnalyst(analystDetail.analyst.id, appliedQuery);
    } catch {
      showError('Não foi possível exportar o Excel');
    } finally {
      setExporting(false);
    }
  };

  const confirmTransfer = async () => {
    if (!transferRow || !toUserId) return;
    try {
      setTransferring(true);
      await cqvVtmeAssignmentService.transfer(transferRow.id, toUserId);
      showSuccess('Grupo transferido e e-mails reenviados');
      setTransferRow(null);
      setToUserId('');
      await load({ reason: 'fresh' });
      if (analystDetail) {
        const detail = await cqvVtmeAssignmentService.getAnalyst(analystDetail.analyst.id, appliedQuery);
        setAnalystDetail(detail);
      }
    } catch (error: any) {
      showError(error.response?.data?.message || 'Não foi possível transferir');
    } finally {
      setTransferring(false);
    }
  };

  if (!isAdmin) {
    return (
      <GlassPageWrapper title="Atribuições VTME" icon="mail">
        <Message error>
          <Message.Header>Acesso Negado</Message.Header>
          <p>Você não tem permissão para acessar esta página.</p>
        </Message>
      </GlassPageWrapper>
    );
  }

  const poolOptions = (data?.poolLoad || []).map(p => ({
    key: p.userId,
    text: `${formatCqvAnalystDisplayName(p.name)} (${p.groupCount})`,
    value: p.userId,
  }));

  const consultantOptions = (data?.consultantOptions || []).map(c => ({
    key: c.userId,
    text: c.name,
    value: c.userId,
  }));

  const generatedAtUtc = data?.generatedAtUtc ?? report?.generatedAtUtc ?? null;
  const snapshotStale = Boolean(data?.snapshotStale || report?.snapshotStale);
  const received = data != null || report != null;
  const fromSnapshot = Boolean(generatedAtUtc);
  const pendingSnapshot = snapshotStale && !generatedAtUtc;
  const periodIsDirect = periodPreset !== 'today';
  const snapshotBanner = describeCqvVtmeSnapshotBanner(
    freshPending
      ? { freshPending: true }
      : freshView && !contentHold && !fromSnapshot && !snapshotStale
        ? {
            freshNow: true,
            periodFromLocal: periodIsDirect ? fromLocal : null,
            periodToLocal: periodIsDirect ? toLocal : null,
          }
        : contentHold && periodIsDirect
          ? {
              directQuery: true,
              periodPending: true,
              periodFromLocal: fromLocal,
              periodToLocal: toLocal,
            }
          : {
              generatedAtUtc,
              nextRefreshAtUtc: data?.nextRefreshAtUtc ?? report?.nextRefreshAtUtc,
              snapshotStale,
              directQuery: !fromSnapshot && !pendingSnapshot && (periodIsDirect || received),
              periodFromLocal: periodIsDirect ? fromLocal : null,
              periodToLocal: periodIsDirect ? toLocal : null,
            }
  );

  return (
    <GlassPageWrapper title="Atribuições VTME" icon="mail">
      <style>{`${CQV_VTME_DROPDOWN_CSS}${CQV_VTME_MODAL_CSS}`}</style>
      <div style={cqvVtmePageStyle}>
        <Segment className="cqv-vtme-filters" style={{ ...cqvVtmeSegmentStyle, marginBottom: 16, overflow: 'visible' }}>
          <Form>
            <CqvVtmePeriodChips value={periodPreset} onChange={applyPeriodPreset} />
            <Form.Group widths="equal">
              <Form.Field>
                <label style={{ color: CQV_VTME_THEME.text }}>Dona</label>
                <Dropdown
                  {...CQV_VTME_SELECT_PROPS}
                  fluid
                  options={poolOptions}
                  value={assignedUserId}
                  placeholder="Todas"
                  noResultsMessage="Nenhuma dona encontrada"
                  onChange={(_, { value }) => setAssignedUserId((value as string) || undefined)}
                />
              </Form.Field>
              <Form.Field>
                <label style={{ color: CQV_VTME_THEME.text }}>Aprovado CQV de</label>
                <Input
                  type="datetime-local"
                  value={fromLocal}
                  disabled={periodPreset !== 'custom'}
                  onChange={e => setFromLocal(e.target.value)}
                />
              </Form.Field>
              <Form.Field>
                <label style={{ color: CQV_VTME_THEME.text }}>até</label>
                <Input
                  type="datetime-local"
                  value={toLocal}
                  disabled={periodPreset !== 'custom'}
                  onChange={e => setToLocal(e.target.value)}
                />
              </Form.Field>
              <Form.Field>
                <label style={{ color: CQV_VTME_THEME.text }}>Vendedor</label>
                <Dropdown
                  {...CQV_VTME_SELECT_PROPS}
                  fluid
                  options={consultantOptions}
                  value={consultantUserId}
                  placeholder="Todos"
                  noResultsMessage="Nenhum vendedor encontrado"
                  onChange={(_, { value }) => setConsultantUserId((value as string) || undefined)}
                />
              </Form.Field>
              <Form.Field>
                <label style={{ color: CQV_VTME_THEME.text }}>Cliente</label>
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="CNPJ ou nome"
                />
              </Form.Field>
              <Form.Field>
                <label style={{ color: CQV_VTME_THEME.text }}>Status</label>
                <Dropdown
                  {...CQV_VTME_SELECT_PROPS}
                  fluid
                  multiple
                  options={STATUS_OPTIONS}
                  value={statuses}
                  placeholder="Todos"
                  noResultsMessage="Nenhum status encontrado"
                  onChange={(_, { value }) => setStatuses((value as number[]) || [])}
                />
              </Form.Field>
            </Form.Group>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
              <Button
                primary
                icon="search"
                content="Filtrar"
                onClick={applyFilters}
                loading={loading && !freshPending}
                disabled={loading}
                style={{ background: CQV_VTME_THEME.accentLilac, color: '#fff' }}
              />
              <Button
                icon="refresh"
                content="Atualizar agora"
                onClick={() => void load({ reason: 'fresh' })}
                loading={freshPending}
                disabled={loading || freshPending}
              />
              <Button
                icon="download"
                content="Exportar lista"
                onClick={exportList}
                loading={exportingList}
                disabled={exportingList || listError}
              />
              <Button
                icon="file excel"
                content="Exportar relatório completo"
                onClick={exportFull}
                loading={exportingFull}
                disabled={exportingFull}
              />
              <CqvVtmeSlaDigestSettings />
            </div>
          </Form>
        </Segment>

        <div
          role="status"
          style={{
            marginBottom: 16,
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            color: snapshotBanner.stale ? '#7a4b12' : CQV_VTME_THEME.text,
            background: snapshotBanner.stale ? '#fff4e5' : '#fff',
            border: `1px solid ${snapshotBanner.stale ? '#e0b15a' : CQV_VTME_THEME.border}`,
            borderLeft: `4px solid ${snapshotBanner.stale ? '#c47b2b' : CQV_VTME_THEME.accentLilac}`,
          }}
        >
          {snapshotBanner.text}
        </div>

        {!contentHold && report?.kpis?.vtmeHistoryAvailable === false ? (
          <Message warning style={{ marginBottom: 16 }}>
            <Message.Header>VTME indisponível — mostrando só dados do GettelWeb</Message.Header>
            <p>
              Não estamos conseguindo confirmar em tempo real o que acontece depois de VTME Criado (Qualidade/CVP,
              BackOffice). Aprovado CQV → VTME Criado continua confiável, pois é calculado só com dados do GettelWeb.
            </p>
          </Message>
        ) : null}

        <Menu pointing secondary style={{ borderColor: CQV_VTME_THEME.border, marginBottom: 16 }}>
          <Menu.Item
            name="Fila"
            active={activeTab === 'fila'}
            onClick={() => setActiveTab('fila')}
            style={{ color: CQV_VTME_THEME.text, fontWeight: 600 }}
          />
          <Menu.Item
            name="Relatórios"
            active={activeTab === 'relatorios'}
            onClick={() => setActiveTab('relatorios')}
            style={{ color: CQV_VTME_THEME.text, fontWeight: 600 }}
          />
        </Menu>

        {contentHold ? (
          <CqvVtmePeriodSkeleton
            variant={activeTab === 'relatorios' ? 'relatorios' : 'fila'}
            analystCount={data?.poolLoad.length || 4}
            message={
              freshPending
                ? 'A buscar os dados de agora. A lista e o relatório entram juntos.'
                : periodIsDirect
                  ? 'Ainda não são os dados deste período. Os cartões só mostram o recorte quando a lista e o relatório chegarem juntos.'
                  : 'A calcular o dia…'
            }
          />
        ) : activeTab === 'relatorios' ? (
          <CqvVtmeReportTab report={report} loadError={reportError} onSelectAnalyst={openAnalyst} />
        ) : listError ? (
          <Segment style={cqvVtmeSegmentStyle}>
            <Message error>
              <Message.Header>Não foi possível carregar a fila</Message.Header>
              <p>Tente filtrar de novo. A aba Relatórios pode continuar disponível se o relatório tiver carregado.</p>
            </Message>
          </Segment>
        ) : (data?.items?.length ?? 0) === 0 ? (
          <>
            <CqvVtmeQueueCards
              poolLoad={data?.poolLoad || []}
              report={report}
              reportError={reportError}
              onSelectAnalyst={openAnalyst}
            />
            <Segment style={cqvVtmeSegmentStyle}>
              <Message info>
                <Message.Header>Nenhuma atribuição VTME</Message.Header>
                <p>Não há grupos no recorte atual. Quando um e-mail VTME for orquestrado, a atribuição aparece aqui.</p>
              </Message>
            </Segment>
          </>
        ) : (
          <>
            <CqvVtmeQueueCards
              poolLoad={data?.poolLoad || []}
              report={report}
              reportError={reportError}
              onSelectAnalyst={openAnalyst}
            />
            <Segment style={cqvVtmeSegmentStyle}>
              <Table celled striped>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Grupo / cliente</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Contratos</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Status</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Aprovado CQV</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>VTME criado</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>IDs dos pedidos</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Dona</Table.HeaderCell>
                    <Table.HeaderCell collapsing style={{ color: CQV_VTME_THEME.text }}>Ações</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(data?.items || []).map(row => {
                    const correctionInfo = row.hasContractInCorrection ? oldestCorrectionContract(row.contracts) : null;
                    return (
                    <Table.Row key={row.id}>
                      <Table.Cell style={{ color: CQV_VTME_THEME.text }}>
                        <div style={{ fontWeight: 600 }}>{row.clientName || 'Grupo'}</div>
                        <div style={{ fontSize: '0.85em', color: CQV_VTME_THEME.muted }}>{row.clientCnpj}</div>
                        {correctionInfo ? (
                          <div style={{ marginTop: 6 }}>
                            <Label
                              size="tiny"
                              title={`Desde ${formatDateTime(correctionInfo.contract.correctionStartedAtUtc)} · ${dash(correctionInfo.contract.correctionStartedByName)}`}
                              style={{ background: CQV_VTME_THEME.accentLilac, color: '#fff' }}
                            >
                              Em correção{correctionInfo.extraCount > 0 ? ` +${correctionInfo.extraCount}` : ''}
                            </Label>
                            <div style={{ fontSize: '0.78em', color: CQV_VTME_THEME.muted, marginTop: 4 }}>
                              desde {formatDateTime(correctionInfo.contract.correctionStartedAtUtc)} ·{' '}
                              {dash(correctionInfo.contract.correctionStartedByName)}
                            </div>
                          </div>
                        ) : null}
                      </Table.Cell>
                      <Table.Cell>
                        <CqvVtmeActionGroup>
                          {row.contracts.map(c => (
                            <a
                              key={c.id}
                              href={`/contracts/${c.id}/cqv-view`}
                              className="cqv-vtme-action-link"
                            >
                              {c.tipoContratacao || 'Contrato'}
                            </a>
                          ))}
                        </CqvVtmeActionGroup>
                      </Table.Cell>
                      <Table.Cell style={{ color: CQV_VTME_THEME.text }}>
                        {row.contracts.map(c => (
                          <div key={`${c.id}-status`}>{c.statusLabel || getContractStatusText(c.status) || '—'}</div>
                        ))}
                      </Table.Cell>
                      <Table.Cell style={{ color: CQV_VTME_THEME.text }}>
                        {row.contracts.map(c => (
                          <div key={`${c.id}-cqv`}>{formatDateTime(c.approvedCqvAt)}</div>
                        ))}
                      </Table.Cell>
                      <Table.Cell style={{ color: CQV_VTME_THEME.text }}>
                        {row.contracts.map(c => (
                          <div key={`${c.id}-vtme`}>{formatDateTime(c.vtmeCreatedAt)}</div>
                        ))}
                      </Table.Cell>
                      <Table.Cell style={{ color: CQV_VTME_THEME.text }}>
                        {row.contracts.map(c => (
                          <div key={`${c.id}-ids`}>
                            {[c.idPedidoContrato1, c.idPedidoContrato2].filter(Boolean).join(' / ') || '—'}
                          </div>
                        ))}
                      </Table.Cell>
                      <Table.Cell style={{ color: CQV_VTME_THEME.text }}>{formatCqvAnalystDisplayName(row.assignedName)}</Table.Cell>
                      <Table.Cell className="cqv-vtme-actions-cell">
                        <CqvVtmeActionGroup>
                          <Button size="small" onClick={() => openHistory(row.id)}>Histórico</Button>
                          {data?.canTransfer && (
                            <Button
                              size="small"
                              primary
                              style={{ background: CQV_VTME_THEME.accentPink, color: '#fff' }}
                              onClick={() => {
                                setTransferRow(row);
                                setToUserId('');
                              }}
                            >
                              Transferir
                            </Button>
                          )}
                          {data?.canTransfer && (
                            <Button size="small" onClick={() => setRemoveTarget(row)}>
                              Remover
                            </Button>
                          )}
                        </CqvVtmeActionGroup>
                      </Table.Cell>
                    </Table.Row>
                    );
                  })}
                </Table.Body>
              </Table>
            </Segment>
          </>
        )}
      </div>

      <Modal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        size="small"
        className="cqv-vtme-modal"
      >
        <Modal.Header style={cqvVtmeModalHeaderStyle}>Histórico do grupo</Modal.Header>
        <Modal.Content style={cqvVtmeModalContentStyle}>
          {historyLoading ? (
            <Loader active inline="centered" />
          ) : (
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Quando</Table.HeaderCell>
                  <Table.HeaderCell>Evento</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {historyEvents.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={2}>Nenhum evento neste grupo.</Table.Cell>
                  </Table.Row>
                ) : (
                  historyEvents.map(ev => (
                    <Table.Row key={ev.id}>
                      <Table.Cell>{formatDateTime(ev.createdAt)}</Table.Cell>
                      <Table.Cell>
                        {eventLabel(ev)}
                        {ev.changeReason ? ` — ${ev.changeReason}` : ''}
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>
          )}
        </Modal.Content>
      </Modal>

      <Modal
        open={!!transferRow}
        onClose={() => setTransferRow(null)}
        size="tiny"
        closeOnEscape
        className="cqv-vtme-modal cqv-vtme-transfer-modal"
      >
        <Modal.Header style={cqvVtmeModalHeaderStyle}>Transferir grupo</Modal.Header>
        <Modal.Content style={{ ...cqvVtmeModalContentStyle, overflow: 'visible' }}>
          <Form>
            <Form.Field>
              <label>Nova dona (pool VTME)</label>
              <Dropdown
                {...CQV_VTME_SELECT_PROPS}
                fluid
                options={(data?.poolLoad || [])
                  .filter(p => p.receivesVtmeEmail !== false && p.userId !== transferRow?.assignedUserId)
                  .map(p => ({ key: p.userId, text: formatCqvAnalystDisplayName(p.name), value: p.userId }))}
                value={toUserId || undefined}
                onChange={(_, { value }) => setToUserId((value as string) || '')}
                placeholder="Selecione"
                noResultsMessage="Nenhuma dona encontrada"
              />
            </Form.Field>
          </Form>
        </Modal.Content>
        <Modal.Actions style={cqvVtmeModalActionsStyle}>
          <Button onClick={() => setTransferRow(null)}>Cancelar</Button>
          <Button
            primary
            loading={transferring}
            disabled={!toUserId}
            onClick={confirmTransfer}
            style={{ background: CQV_VTME_THEME.accentLilac, color: '#fff' }}
          >
            Confirmar
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={analystOpen}
        onClose={() => setAnalystOpen(false)}
        size="large"
        className="cqv-vtme-modal"
        closeIcon
      >
        <Modal.Header style={cqvVtmeModalHeaderStyle}>
          {analystDetail ? formatCqvAnalystDisplayName(analystDetail.analyst.name) : 'Detalhe da analista'}
        </Modal.Header>
        <Modal.Content scrolling style={cqvVtmeModalContentStyle}>
          {analystLoading ? (
            <Loader active inline="centered" />
          ) : (
            <>
              <Message info>
                <p style={{ color: CQV_VTME_THEME.text, margin: 0 }}>{CQV_VTME_ANALYST_DISCLAIMER}</p>
              </Message>
              <Table celled striped compact>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Cliente</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Papel</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Status</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Linhas</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Dona atual</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Crédito VTME</Table.HeaderCell>
                    <Table.HeaderCell style={{ color: CQV_VTME_THEME.text }}>Aberto</Table.HeaderCell>
                    <Table.HeaderCell collapsing style={{ color: CQV_VTME_THEME.text }}>Ações</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {(analystDetail?.items || []).length === 0 ? (
                    <Table.Row>
                      <Table.Cell colSpan={8} style={{ color: CQV_VTME_THEME.text }}>
                        Nenhum grupo neste recorte.
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    (analystDetail?.items || []).map(item => {
                      const canManageCurrent = !!data?.canTransfer && item.isActive && item.relation === 'current';
                      return (
                        <Table.Row key={item.id}>
                          <Table.Cell style={{ color: CQV_VTME_THEME.text }}>
                            <div style={{ fontWeight: 600 }}>{dash(item.clientName)}</div>
                            <div style={{ fontSize: '0.85em', color: CQV_VTME_THEME.muted }}>{dash(item.clientCnpj)}</div>
                          </Table.Cell>
                          <Table.Cell style={{ color: CQV_VTME_THEME.text }}>
                            {relationLabel(item.relation)}
                            {item.statusMark ? ` · ${item.statusMark}` : ''}
                          </Table.Cell>
                          <Table.Cell style={{ color: CQV_VTME_THEME.text }}>
                            {item.contracts.map(c => (
                              <div key={`${c.id}-st`}>{c.statusLabel || getContractStatusText(c.status) || '—'}</div>
                            ))}
                          </Table.Cell>
                          <Table.Cell style={{ color: CQV_VTME_THEME.text }}>
                            {item.contracts.map(c => (
                              <div key={`${c.id}-lines`} style={{ marginBottom: 4 }}>
                                <CqvVtmeLinesSummary
                                  aditivo={c.linesAditivo}
                                  renegociacao={c.linesRenegociacao}
                                  apta={c.linesRenegociacaoApta}
                                  naoApta={c.linesRenegociacaoNaoApta}
                                  semMarcacao={c.linesRenegociacaoSemMarcacao}
                                  compact
                                />
                              </div>
                            ))}
                          </Table.Cell>
                          <Table.Cell style={{ color: CQV_VTME_THEME.text }}>
                            {formatCqvAnalystDisplayName(item.assignedName)}
                          </Table.Cell>
                          <Table.Cell style={{ color: CQV_VTME_THEME.text }}>{dash(item.creditVtmeName)}</Table.Cell>
                          <Table.Cell style={{ color: CQV_VTME_THEME.text }}>
                            {item.isOpenForAnalyst ? (item.isOpenOlderThan48hForAnalyst ? 'Sim · > 48 h' : 'Sim') : 'Não'}
                          </Table.Cell>
                          <Table.Cell className="cqv-vtme-actions-cell">
                            <CqvVtmeActionGroup>
                              {canManageCurrent && (
                                <Button
                                  size="small"
                                  primary
                                  style={{ background: CQV_VTME_THEME.accentPink, color: '#fff' }}
                                  onClick={() => {
                                    setTransferRow(item);
                                    setToUserId('');
                                  }}
                                >
                                  Transferir
                                </Button>
                              )}
                              {canManageCurrent && (
                                <Button size="small" onClick={() => setRemoveTarget(item)}>
                                  Remover
                                </Button>
                              )}
                              <Button size="small" onClick={() => openHistory(item.id)}>Histórico</Button>
                              {item.contracts.map(c => (
                                <a
                                  key={c.id}
                                  href={`/contracts/${c.id}/cqv-view`}
                                  className="cqv-vtme-action-link"
                                >
                                  {c.tipoContratacao || 'Contrato'}
                                </a>
                              ))}
                            </CqvVtmeActionGroup>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })
                  )}
                </Table.Body>
              </Table>
            </>
          )}
        </Modal.Content>
        <Modal.Actions style={cqvVtmeModalActionsStyle}>
          <Button onClick={() => setAnalystOpen(false)}>Fechar</Button>
          <Button
            primary
            loading={exporting}
            disabled={!analystDetail || analystLoading}
            onClick={exportAnalyst}
            style={{ background: CQV_VTME_THEME.accentLilac, color: '#fff' }}
          >
            Exportar Excel
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        size="tiny"
        className="cqv-vtme-modal"
      >
        <Modal.Header style={cqvVtmeModalHeaderStyle}>Remover atribuição</Modal.Header>
        <Modal.Content style={cqvVtmeModalContentStyle}>
          <p style={{ color: CQV_VTME_THEME.text }}>
            Remover tira o grupo da fila. O contrato não é cancelado. O histórico fica.
          </p>
        </Modal.Content>
        <Modal.Actions style={cqvVtmeModalActionsStyle}>
          <Button onClick={() => setRemoveTarget(null)}>Cancelar</Button>
          <Button
            primary
            loading={removing}
            onClick={confirmRemove}
            style={{ background: CQV_VTME_THEME.accentPink, color: '#fff' }}
          >
            Remover
          </Button>
        </Modal.Actions>
      </Modal>
    </GlassPageWrapper>
  );
};

export default CqvVtmeAssignmentsPage;
